import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/store';
import { transcribeVideo, TranscriptionSegment } from '../lib/whisper';
import { cutVideo } from '../lib/ffmpeg';
import { Upload, Loader2, Download, Scissors, CheckCircle, Lock } from 'lucide-react';

export default function ProcessingPage() {
  const navigate = useNavigate();
  const { user, subscription } = useAuthStore();
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [step, setStep] = useState<'upload' | 'transcribing' | 'transcribed' | 'cutting' | 'done'>('upload');
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [transcript, setTranscript] = useState<TranscriptionSegment[]>([]);
  const [selectedClip, setSelectedClip] = useState<{ start: number; end: number } | null>(null);
  const [outputBlob, setOutputBlob] = useState<Blob | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const hasActiveSubscription = subscription?.active && subscription?.expiry && subscription.expiry > Date.now();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
      setStep('upload');
    }
  };

  const handleTranscribe = async () => {
    if (!videoFile) return;

    setStep('transcribing');
    setProgress(0);
    setStatusMessage('Kutoa sauti kutoka video...');

    try {
      const segments = await transcribeVideo(videoFile, (progress, stage) => {
        setProgress(progress);
        if (stage === 'extracting') {
          setStatusMessage('Kutoa sauti kutoka video (FFmpeg)...');
        } else if (stage === 'decoding') {
          setStatusMessage('Kuandaa audio kwa Whisper...');
        } else if (stage === 'transcribing') {
          setStatusMessage('Kutambua maneno (Whisper AI)...');
        }
      });
      setTranscript(segments);
      setStatusMessage('');
      setStep('transcribed');
    } catch (error) {
      console.error('Transcription error:', error);
      const msg = error instanceof Error ? error.message : 'Unknown error';
      alert(`Hitilafu transcription: ${msg}`);
      setStatusMessage('');
      setStep('upload');
    }
  };

  const handleSelectClip = (start: number, end: number) => {
    setSelectedClip({ start, end });
  };

  const handleCutVideo = async () => {
    if (!videoFile || !selectedClip) return;

    setStep('cutting');
    setProgress(0);

    try {
      const duration = selectedClip.end - selectedClip.start;
      const blob = await cutVideo(videoFile, selectedClip.start, duration, '9:16');
      setOutputBlob(blob);
      setStep('done');
    } catch (error) {
      console.error('Cutting error:', error);
      const msg = error instanceof Error ? error.message : 'Unknown error';
      alert(`Hitilafu kukata video: ${msg}`);
      setStep('transcribed');
    }
  };

  const handleDownload = () => {
    if (!outputBlob) return;

    const url = URL.createObjectURL(outputBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clipd-${Date.now()}.mp4`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  // Show locked overlay if subscription is not active
  if (!hasActiveSubscription && user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <Lock className="w-20 h-20 text-slate-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              Features Zimefungwa
            </h2>
            <p className="text-slate-600 mb-6">
              Tafadhali lipia TSh 5,000 kufungua huduma zote za CLIPD kwa masaa 24.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-blue-900 mb-2">Unapata nini?</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✓ Video processing unlimited</li>
                <li>✓ AI transcription (Whisper)</li>
                <li>✓ Smart clipping</li>
                <li>✓ Export 9:16, 1:1, 16:9</li>
                <li>✓ 100% local processing</li>
              </ul>
            </div>
            
            <button
              onClick={() => navigate('/payment')}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold"
            >
              Lipia TSh 5,000 Sasa
            </button>
            
            <p className="text-xs text-slate-500 mt-4">
              M-Pesa, Tigo Pesa, Airtel Money
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Subscription Status Banner */}
        {hasActiveSubscription && subscription?.expiry && user.role !== 'admin' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-900">Subscription Active</p>
                <p className="text-sm text-green-700">
                  Inakisha: {new Date(subscription.expiry).toLocaleString('sw-TZ')}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/payment')}
              className="text-sm text-green-700 hover:text-green-900 underline"
            >
              Lipia tena
            </button>
          </div>
        )}
        
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            CLIPD Processing
          </h1>
          <p className="text-slate-600 mt-2">Video haipandi server - sauti tu ndio inatumwa kwa transcription</p>
        </div>

        {/* Upload Step */}
        {step === 'upload' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center">
              <Upload className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Chagua Video au Audio
              </h2>
              <p className="text-slate-600 mb-6">
                Sauti itatolewa kwenye video kwenye browser yako, kisha kutumwa kwa transcription. Video haipandi server.
              </p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="video/*,audio/*,.mp3,.wav,.m4a,.ogg,.flac,.aac,.wma,.mp4,.mkv,.avi,.mov,.webm"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold"
              >
                Chagua Faili
              </button>
              
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <h3 className="font-semibold text-blue-900 mb-2 text-sm">Formats Zinazokubaliwa:</h3>
                <div className="grid grid-cols-2 gap-2 text-xs text-blue-800">
                  <div>
                    <strong>Video:</strong> MP4, MKV, AVI, MOV, WebM
                  </div>
                  <div>
                    <strong>Audio:</strong> MP3, WAV, M4A, OGG, FLAC, AAC, WMA
                  </div>
                </div>
              </div>
              
              {videoFile && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>Faili:</strong> {videoFile.name}
                  </p>
                  <p className="text-sm text-blue-800">
                    <strong>Ukubwa:</strong> {(videoFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  <button
                    onClick={handleTranscribe}
                    className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                  >
                    Anza Transcription
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Transcribing Step */}
        {step === 'transcribing' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <Loader2 className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Inafanya Transcription...
            </h2>
            <p className="text-slate-600 mb-2">
              {statusMessage || 'Whisper AI inafanya kazi kwenye browser yako'}
            </p>
            <div className="w-full bg-slate-200 rounded-full h-4 mb-4">
              <div
                className="bg-gradient-to-r from-blue-600 to-purple-600 h-4 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-slate-500">{progress.toFixed(0)}%</p>
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
              <h3 className="font-semibold text-blue-900 mb-2 text-sm">Hatua:</h3>
              <ul className="text-xs text-blue-800 space-y-1">
                <li className={statusMessage.includes('FFmpeg') ? 'font-bold' : ''}>
                  {statusMessage.includes('FFmpeg') ? '→' : '✓'} 1. Toa sauti kutoka video (FFmpeg)
                </li>
                <li className={statusMessage.includes('Kuandaa') ? 'font-bold' : ''}>
                  {statusMessage.includes('Kuandaa') ? '→' : progress > 30 ? '✓' : '○'} 2. Andaa audio kwa Whisper
                </li>
                <li className={statusMessage.includes('Kutambua') ? 'font-bold' : ''}>
                  {statusMessage.includes('Kutambua') ? '→' : progress > 50 ? '✓' : '○'} 3. Tambua maneno (Whisper AI)
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Transcribed Step */}
        {step === 'transcribed' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <h2 className="text-2xl font-bold text-slate-900">
                Transcription Imekamilika
              </h2>
            </div>
            <p className="text-slate-600 mb-6">
              Chagua sehemu ya video unayotaka kukata (15-30 sekunde)
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-96 overflow-y-auto mb-6">
              {transcript.map((segment, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSelectClip(segment.start, segment.end)}
                  className={`p-3 mb-2 rounded-lg cursor-pointer transition-colors ${
                    selectedClip?.start === segment.start
                      ? 'bg-blue-100 border-2 border-blue-500'
                      : 'bg-white border border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-mono text-blue-600">
                      [{segment.start.toFixed(1)}s - {segment.end.toFixed(1)}s]
                    </span>
                    <span className="text-xs text-slate-500">
                      {(segment.end - segment.start).toFixed(1)}s
                    </span>
                  </div>
                  <p className="text-sm text-slate-800">{segment.text}</p>
                </div>
              ))}
            </div>
            {selectedClip && (
              <button
                onClick={handleCutVideo}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold flex items-center justify-center gap-2"
              >
                <Scissors className="w-5 h-5" />
                Kata Video (9:16 Vertical)
              </button>
            )}
          </div>
        )}

        {/* Cutting Step */}
        {step === 'cutting' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <Loader2 className="w-16 h-16 text-purple-600 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Inakata Video...
            </h2>
            <p className="text-slate-600 mb-6">
              FFmpeg.WASM inafanya kazi kwenye browser yako
            </p>
            <div className="w-full bg-slate-200 rounded-full h-4 mb-4">
              <div
                className="bg-gradient-to-r from-purple-600 to-blue-600 h-4 rounded-full animate-pulse"
                style={{ width: '50%' }}
              />
            </div>
          </div>
        )}

        {/* Done Step */}
        {step === 'done' && outputBlob && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Video Imekamilika!
            </h2>
            <p className="text-slate-600 mb-6">
              Clip yako ya 9:16 vertical iko tayari kwa download
            </p>
            <button
              onClick={handleDownload}
              className="px-8 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg hover:from-green-700 hover:to-blue-700 transition-all font-semibold flex items-center justify-center gap-2 mx-auto"
            >
              <Download className="w-5 h-5" />
              Download Video
            </button>
            <button
              onClick={() => {
                setStep('upload');
                setVideoFile(null);
                setTranscript([]);
                setSelectedClip(null);
                setOutputBlob(null);
              }}
              className="mt-4 text-blue-600 hover:underline"
            >
              Process Video Nyingine
            </button>
          </div>
        )}

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-2">
            🔒 Usiri ni Mtakatifu
          </h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>✓ Video yako haipandi server kamwe</li>
            <li>✓ Kila kitu kinafanyika kwenye browser yako</li>
            <li>✓ Hakuna data yako inayohifadhiwa popote</li>
            <li>✓ 100% local processing na WASM</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
